import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '../../core/store';
import { getMaterial } from '../materials';

function drawJerseyCanvas(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  name: string,
  number: number,
  nameOpacity = 1,
) {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, '#ee5068');
  g.addColorStop(0.3, '#d02440');
  g.addColorStop(0.65, '#a01830');
  g.addColorStop(1, '#5a0c1a');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  for (let y = 0; y < h; y += 2) {
    ctx.fillStyle = y % 4 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
    ctx.fillRect(0, y, w, 1);
  }

  // Cloth fold suggestion
  for (let i = 0; i < 8; i++) {
    const x = w * (0.15 + i * 0.09);
    ctx.strokeStyle = `rgba(0,0,0,${0.04 + (i % 2) * 0.03})`;
    ctx.beginPath();
    ctx.moveTo(x, h * 0.1);
    ctx.quadraticCurveTo(x + 8, h * 0.5, x - 4, h * 0.9);
    ctx.stroke();
  }

  ctx.fillStyle = '#f5f7fa';
  ctx.fillRect(0, 0, w, h * 0.05);
  ctx.fillStyle = 'rgba(245,247,250,0.95)';
  ctx.fillRect(w * 0.05, h * 0.07, w * 0.045, h * 0.8);
  ctx.fillRect(w * 0.905, h * 0.07, w * 0.045, h * 0.8);

  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 12;
  ctx.fillStyle = '#f5f7fa';
  ctx.font = `bold ${Math.floor(h * 0.44)}px Bebas Neue, Impact, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(number), w * 0.5, h * 0.36);

  if (name && nameOpacity > 0.01) {
    const label = name;
    const nameSize =
      label.length > 12 ? Math.floor(h * 0.072) : Math.floor(h * 0.095);
    // Soft projected glow under letters (print light)
    ctx.globalAlpha = Math.max(0, Math.min(1, nameOpacity)) * 0.35;
    ctx.shadowColor = 'rgba(255, 230, 160, 0.8)';
    ctx.shadowBlur = 18;
    ctx.fillStyle = '#ffe8a8';
    ctx.font = `bold ${nameSize}px Bebas Neue, Impact, sans-serif`;
    ctx.fillText(label, w * 0.5, h * 0.72);
    // Stitched print
    ctx.globalAlpha = Math.max(0, Math.min(1, nameOpacity));
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 8;
    ctx.fillStyle = '#f5f7fa';
    ctx.fillText(label, w * 0.5, h * 0.72);
    // Micro stitch dashes
    ctx.globalAlpha = Math.max(0, Math.min(1, nameOpacity)) * 0.45;
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);
    const tw = ctx.measureText(label).width;
    ctx.beginPath();
    ctx.moveTo(w * 0.5 - tw * 0.5, h * 0.78);
    ctx.lineTo(w * 0.5 + tw * 0.5, h * 0.78);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }
  ctx.shadowBlur = 0;
}

export function useJerseyTexture(opts?: {
  forceName?: string | null;
  nameOpacity?: number;
}) {
  const profileName = useGameStore((s) => s.profile.name);
  const profileNumber = useGameStore((s) => s.profile.number);
  const showReal = useGameStore((s) => s.showRealIdentity);
  const forced = opts?.forceName;
  const nameOpacity = opts?.nameOpacity ?? 1;
  const name =
    forced !== undefined
      ? forced || ''
      : showReal
        ? 'ADARSH DHAWALE'
        : profileName;
  const number = showReal || forced === 'ADARSH DHAWALE' ? 10 : profileNumber;

  const { canvas, texture } = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.flipY = true;
    return { canvas, texture };
  }, []);

  useEffect(() => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawJerseyCanvas(
      ctx,
      canvas.width,
      canvas.height,
      name || '',
      number,
      nameOpacity,
    );
    texture.needsUpdate = true;
  }, [canvas, texture, name, number, nameOpacity]);

  useEffect(
    () => () => {
      texture.dispose();
    },
    [texture],
  );

  return texture;
}

/** Kit with boots, shin guards, gloves + breathing — procedural, not MetaHuman. */
export function JerseyMesh({
  position = [0, 0.95, 0] as [number, number, number],
  forceName,
  nameOpacity = 1,
  scale = 1,
}: {
  position?: [number, number, number];
  forceName?: string | null;
  nameOpacity?: number;
  scale?: number;
}) {
  const texture = useJerseyTexture({ forceName, nameOpacity });
  const group = useRef<THREE.Group>(null);
  const fabric = useMemo(() => {
    const m = getMaterial('jerseyFabric', { scale: 1.8 }).clone();
    m.map = texture;
    m.roughness = 0.72;
    m.metalness = 0.06;
    m.normalScale = new THREE.Vector2(1.35, 1.35);
    m.envMapIntensity = 1.05;
    m.needsUpdate = true;
    return m;
  }, [texture]);
  const shorts = useMemo(() => getMaterial('shorts'), []);
  const skin = useMemo(() => {
    const m = getMaterial('skin').clone();
    m.roughness = 0.55;
    m.normalScale = new THREE.Vector2(1.1, 1.1);
    return m;
  }, []);
  const boot = useMemo(
    () => getMaterial('floodMetal', { tint: [0.08, 0.08, 0.1] }),
    [],
  );
  const glove = useMemo(
    () => getMaterial('ballLeather', { tint: [0.92, 0.92, 0.9] }),
    [],
  );

  useFrame(({ clock }) => {
    if (!group.current) return;
    const breathe = Math.sin(clock.elapsedTime * 1.35) * 0.012;
    group.current.scale.set(
      scale * (1 + breathe * 0.35),
      scale * (1 + breathe),
      scale * (1 + breathe * 0.25),
    );
    group.current.rotation.y = Math.PI + Math.sin(clock.elapsedTime * 0.35) * 0.04;
  });

  return (
    <group ref={group} position={position} rotation={[0, Math.PI, 0]}>
      <mesh castShadow material={fabric} position={[0, 0.82, 0]}>
        <torusGeometry args={[0.2, 0.045, 10, 24]} />
      </mesh>

      <mesh castShadow material={fabric} position={[0, 0.5, 0]} scale={[1, 1, 0.62]}>
        <boxGeometry args={[0.58, 0.62, 0.42]} />
      </mesh>
      <mesh castShadow material={fabric} position={[0, 0.5, 0]} scale={[1.02, 1.02, 0.55]}>
        <capsuleGeometry args={[0.27, 0.28, 10, 22]} />
      </mesh>

      {/* Name/number plate — facing camera after group Y=π */}
      <mesh castShadow material={fabric} position={[0, 0.52, 0.22]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[0.52, 0.62]} />
      </mesh>

      <mesh castShadow material={fabric} position={[-0.36, 0.62, 0]} rotation={[0, 0, 1.05]}>
        <cylinderGeometry args={[0.1, 0.12, 0.28, 14]} />
      </mesh>
      <mesh castShadow material={fabric} position={[0.36, 0.62, 0]} rotation={[0, 0, -1.05]}>
        <cylinderGeometry args={[0.1, 0.12, 0.28, 14]} />
      </mesh>

      <mesh castShadow material={skin} position={[-0.48, 0.38, 0.04]} rotation={[0.2, 0, 0.25]}>
        <cylinderGeometry args={[0.055, 0.06, 0.28, 10]} />
      </mesh>
      <mesh castShadow material={skin} position={[0.48, 0.38, 0.04]} rotation={[0.2, 0, -0.25]}>
        <cylinderGeometry args={[0.055, 0.06, 0.28, 10]} />
      </mesh>
      <mesh castShadow material={glove} position={[-0.55, 0.2, 0.08]} rotation={[0.4, 0, 0.2]}>
        <boxGeometry args={[0.1, 0.12, 0.14]} />
      </mesh>
      <mesh castShadow material={glove} position={[0.55, 0.2, 0.08]} rotation={[0.4, 0, -0.2]}>
        <boxGeometry args={[0.1, 0.12, 0.14]} />
      </mesh>

      <mesh castShadow material={shorts} position={[0, 0.12, 0]} scale={[1.05, 1, 0.78]}>
        <boxGeometry args={[0.52, 0.28, 0.36]} />
      </mesh>

      <mesh castShadow material={skin} position={[-0.12, -0.18, 0]}>
        <cylinderGeometry args={[0.085, 0.09, 0.36, 12]} />
      </mesh>
      <mesh castShadow material={skin} position={[0.12, -0.18, 0]}>
        <cylinderGeometry args={[0.085, 0.09, 0.36, 12]} />
      </mesh>
      <mesh castShadow material={fabric} position={[-0.12, -0.32, 0.02]}>
        <boxGeometry args={[0.14, 0.2, 0.1]} />
      </mesh>
      <mesh castShadow material={fabric} position={[0.12, -0.32, 0.02]}>
        <boxGeometry args={[0.14, 0.2, 0.1]} />
      </mesh>

      <mesh castShadow material={boot} position={[-0.12, -0.52, 0.04]} rotation={[0.15, 0, 0]}>
        <boxGeometry args={[0.14, 0.1, 0.28]} />
      </mesh>
      <mesh castShadow material={boot} position={[0.12, -0.52, 0.04]} rotation={[0.15, 0, 0]}>
        <boxGeometry args={[0.14, 0.1, 0.28]} />
      </mesh>
    </group>
  );
}
